<script setup>
import { computed, ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import ProductItemSmall from './ProductItemSmall.vue';
import ProductItem from './ProductItem.vue';

const router = useRouter();
const route = useRoute();

const props = defineProps({
  modelValue: Object,
  scenes: Array,
  alpha: {
    type: Boolean,
    default: false,
  },
  disableNavigation: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:model-value']);
const containerRef = ref(null);
const showRightArrow = ref(false);
const showLeftArrow = ref(false);
const isHovered = ref(false);
let hideTimeout = null;

const selectedScene = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:model-value', v),
});

const visibleScenes = computed(() => {
  const list = props.scenes || [];
  return list.filter((item) => item.visibility !== 'hidden');
});

const mainScenes = computed(() => {
  const list = visibleScenes.value;
  return list.filter((item) => item.type === 'main');
});

const smallScenes = computed(() => {
  const list = visibleScenes.value;
  return list.filter((item) => item.type !== 'main');
});

// Scroll to center the selected card
const scrollToCenter = (sceneName) => {
  nextTick(() => {
    if (containerRef.value) {
      const selectedElement = containerRef.value.querySelector(`[data-product="${sceneName}"]`);
      if (selectedElement) {
        const containerWidth = containerRef.value.clientWidth;
        const elementLeft = selectedElement.offsetLeft;
        const elementWidth = selectedElement.offsetWidth;

        // Center the selected element in the container
        containerRef.value.scrollTo({
          left: elementLeft - containerWidth / 2 + elementWidth / 2,
          behavior: 'smooth',
        });
      }
    }
  });
};

const selectScene = (scene) => {
  selectedScene.value = scene;
  // Scroll to center the clicked card
  scrollToCenter(scene.name);

  // Only navigate if navigation is not disabled and path is not '/'
  if (!props.disableNavigation && route.path !== '/') {
    if (props.alpha) {
      router.push(`/create/alpha/${scene.name}`);
    } else {
      router.push(`/create/${scene.name}`);
    }
  }
};

const scrollRight = () => {
  if (containerRef.value) {
    containerRef.value.scrollBy({ left: 400, behavior: 'smooth' });
  }
};

const scrollLeft = () => {
  if (containerRef.value) {
    containerRef.value.scrollBy({ left: -400, behavior: 'smooth' });
  }
};

const checkScroll = () => {
  if (!containerRef.value) return;
  const container = containerRef.value;

  // Check for right arrow visibility
  if (container.scrollLeft + container.clientWidth >= container.scrollWidth - 10) {
    showRightArrow.value = false;
  } else {
    showRightArrow.value = true;
  }

  // Check for left arrow visibility
  if (container.scrollLeft > 10) {
    showLeftArrow.value = true;
  } else {
    showLeftArrow.value = false;
  }
};

watch(
  () => props.scenes,
  () => {
    nextTick(() => {
      checkScroll();
    });
  }
);

onMounted(() => {
  checkScroll();
  window.addEventListener('resize', checkScroll);
  containerRef.value?.addEventListener('scroll', checkScroll);

  // Scroll to the currently selected item on mount
  if (selectedScene.value) {
    scrollToCenter(selectedScene.value.name);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkScroll);
  containerRef.value?.removeEventListener('scroll', checkScroll);
  // Clean up timeout
  if (hideTimeout) {
    clearTimeout(hideTimeout);
    hideTimeout = null;
  }
});

const handleMouseEnter = () => {
  // Clear any pending hide timeout
  if (hideTimeout) {
    clearTimeout(hideTimeout);
    hideTimeout = null;
  }
  isHovered.value = true;
};

const handleMouseLeave = () => {
  // Delay hiding arrows by 1 second
  hideTimeout = setTimeout(() => {
    isHovered.value = false;
    hideTimeout = null;
  }, 1000);
};
</script>

<template>
  <div class="relative" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
    <!-- Left scroll arrow -->
    <div
      v-if="showLeftArrow"
      :class="[
        'absolute left-2 top-1/2 transform -translate-y-1/2 z-10 transition-opacity duration-400',
        isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none',
      ]"
    >
      <div
        class="w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center cursor-pointer"
        @click="scrollLeft"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-black/60"
        >
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </div>
    </div>

    <div class="overflow-x-auto relative scrollbar-hide overscroll-x-auto" ref="containerRef">
      <div class="flex space-x-0.5 py-2">
        <ProductItem
          v-for="product in mainScenes"
          :product="product"
          :selectedScene="selectedScene"
          @click="selectScene(product)"
        />

        <!-- Small scenes section with horizontal layout -->
        <ProductItemSmall
          v-for="product in smallScenes"
          :product="product"
          :selected-scene="selectedScene"
          @click="selectScene(product)"
        />
      </div>
    </div>

    <!-- Right scroll arrow -->
    <div
      v-if="showRightArrow"
      :class="[
        'absolute right-2 top-1/2 transform -translate-y-1/2 z-10 transition-opacity duration-400',
        isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none',
      ]"
    >
      <div
        class="w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center cursor-pointer"
        @click="scrollRight"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-black/60"
        >
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </div>
    </div>
  </div>
</template>
